import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connectHits, connectStateResults } from 'react-instantsearch-dom';
import { Loading, Typography } from 'ck-ui';
import { findDupesInArray, scriptTagToHash } from 'ck-utils';
import { trackProductImpressions } from 'ck-utils/algoliaAnalytics';
import { Label } from '../../utils';
import { BundleLookPageProvider } from '../../context';
import { CuratedProductList } from './CuratedProductList';

const CuratedBundleContainer = ({
  listName,
  featureImage,
  bundle,
  isSearchStalled,
  searchResults,
  hits
}) => {
  const { nbHits, queryID, hitsPerPage, page, hits: sHits, index: indexName } = searchResults || {};

  // Fire product impression tracking on when query ID changes
  useEffect(() => {
    if (queryID) {
      trackProductImpressions(sHits, hitsPerPage, page, listName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryID]);

  // Render Loading
  if (isSearchStalled && !nbHits) {
    return <Loading isLoading />;
  }

  // Render No results
  if (!nbHits) {
    return (
      <div>
        <Typography renderAs="h1" display="inline" variant="heading4">
          Sorry, we couldn't find any products
        </Typography>
      </div>
    );
  }

  /**
   * Extract the current bundle for the collection from all the bundle tags. The bundle tag that appears the most is the current one
   * @type {object} An Object contains all the bundles and highlights the current bundle
   */
  const { current: currentBundle } = hits.reduce((result, hit) => hit.tags.reduce((acc, tag) => {
    if (tag.indexOf('script=bundle') === -1) return acc;
    const { id, index, indexUnit = 1 } = scriptTagToHash(tag);
    const currentGroupIndex = Number(index) - 1;
    const { current } = acc;
    const { groupsCount = [], groupsHits = [], groupsCategories = [], count, hasMultiProductsPerGroup = false } = acc[id] || {};
    const currentGroupHits = groupsHits[currentGroupIndex] || [];
    const { categories = [], subCategories = [] } = groupsCategories[currentGroupIndex] || {};
    const bundleObj = {
      id,
      hasMultiProductsPerGroup: hasMultiProductsPerGroup || currentGroupHits.length > 0,
      groupsCount: Object.assign([], groupsCount, { [currentGroupIndex]: indexUnit }),
      groupsHits: Object.assign([], groupsHits, { [currentGroupIndex]: [...currentGroupHits, hit] }),
      groupsCategories: Object.assign([], groupsCategories, {
        [currentGroupIndex]: {
          categories: [...categories, ...hit.category.split(',')],
          subCategories: [...subCategories, ...new Set(hit.subCategory.split(','))]
        }
      }),
      count: count ? count + 1 : 1,
    };
    return {
      ...acc,
      [id]: bundleObj,
      // eslint-disable-next-line no-nested-ternary
      current: current ? (current.count < bundleObj.count ? bundleObj : current) : bundleObj
    };
  }, result), {});

  // Don't render if currentBundle is not valid
  if (!currentBundle) return null;

  const { groupsCount, groupsCategories, groupsHits, hasMultiProductsPerGroup } = currentBundle;

  /**
   * Normalise the bundle data for the BYO header, including :
   * group_count - array of product count needed to add to the cart for each group(index), example: ['1', '1', '1']
   * icon - array of icon code for each group(index), example: ['hoodie', 'hat', 'shoe']
   * name - array of name for the group(index), example: ['tops', 'headwear', 'footwear']
   * tag - array of tag to identify each group(index), TODO: may not need this one if we can refactor ByoHeader
   * @type {object}
   */
  const bundleGroupData = {
    group_count: groupsCount,
    ...groupsCategories.reduce((result, group, index) => {
      const groupHits = groupsHits[index];
      const { categories, subCategories } = group;

      // Find common categories only if the group has more than one products
      const commonCategories = groupHits.length > 1 ? findDupesInArray(categories) : categories;
      const commonSubCategories = groupHits.length > 1 ? findDupesInArray(subCategories) : subCategories;

      // Group gender priority, mens > womens > kids (rest)
      const groupGender = groupHits.reduce((gender, hit) => {
        if (gender === 'mens' || hit.gender.includes('mens')) return 'mens';
        if (gender === 'womens' || hit.gender.includes('womens')) return 'womens';
        return hit.gender[0];
      }, '');

      let icon = null;
      let name = null;

      // Check Subcategories first to get the group's icon and name
      if (commonSubCategories.length > 0) {
        icon = commonSubCategories.reduce((acc, category) => Label.getIconCode(category, groupGender) || acc, icon);
        name = commonSubCategories.reduce((acc, category) => {
          const categoryName = Label.getLocaleString(category, 'subcategory');
          // Get the shorter name as the title because spaces are limited
          if (acc && acc.length <= categoryName.length) return acc;
          return categoryName;
        }, name);
      }

      // Check main category second to compare name length and if icon is not mapped in subCategory, map it from main category
      if (commonCategories.length > 0) {
        name = commonCategories.reduce((acc, category) => {
          const categoryName = Label.getLocaleString(category, 'category');
          // Get the shorter name as the title because spaces are limited
          if (acc && acc.length <= categoryName.length) return acc;
          return categoryName;
        }, name);

        if (!icon) {
          icon = commonCategories.reduce((acc, category) => Label.getIconCode(category, groupGender) || acc, icon);
        }
      }

      return {
        icon: [...result.icon, icon || 'ck-logo'],
        name: [...result.name, name],
        tag: [...result.tag, `${icon}_${index}`]// to match the ByoTopNavigation API, TODO: CONSIDER REMOVING THIS
      };
    }, { icon: [], name: [], tag: [] })
  };

  return (
    <BundleLookPageProvider bundle={bundle} byoBundle={bundleGroupData}>
      <CuratedProductList
        indexName={indexName}
        queryID={queryID}
        listName={listName}
        featureImage={featureImage}
        groupsHits={groupsHits}
        groupNames={bundleGroupData.name}
        groupIcons={bundleGroupData.icon}
        hasMultiProductsPerGroup={hasMultiProductsPerGroup}
      />
    </BundleLookPageProvider>
  );
};

CuratedBundleContainer.propTypes = {
  featureImage: PropTypes.string.isRequired,
  bundle: PropTypes.object.isRequired,
  hits: PropTypes.array,
  isSearchStalled: PropTypes.bool.isRequired,
  listName: PropTypes.string.isRequired,
  searchResults: PropTypes.object
};

CuratedBundleContainer.defaultProps = {
  hits: [],
  searchResults: null
};

const CuratedBundle = connectHits(connectStateResults(CuratedBundleContainer));
export { CuratedBundle };

export default CuratedBundle;
